import type {
  Indicator,
  IndicatorResponse,
  Item,
  ScoringLevel,
} from '@/lib/schema/types';
import { SCORED_LEVELS } from '@/lib/schema/loader';

/**
 * Item scoring — implements ers_schema_v1.0.json `scoring_algorithm.rules`.
 *
 * Y / N / NA:
 *   - Y = Yes (met)
 *   - N = No (not met)
 *   - NA = Not applicable (only where `indicator.allows_na` or `item.item_allows_na`)
 *
 * NA handling (see schema `na_rules`):
 *   NA-scored indicators are excluded from BOTH the "all must be Yes" and the
 *   "at least half must be Yes" rules — we only require the *scoreable*
 *   indicators at a level to be Yes. The manual states this without
 *   qualification: "Indicators that are scored NA are not counted when
 *   determining the score for an Item" (Scoring System, point 8, p. 11).
 *   This is also surfaced in the app `?` help popover.
 *
 * Score meanings (labels):
 *   1 = Inadequate · 3 = Minimal · 5 = Good · 7 = Excellent
 *   2, 4, 6 are transitional.
 *
 * Returned score is the highest rule matched walking from 7 down to 1.
 * If required evidence at a given level is still unanswered, we bail with
 * `null` (unscored) so the UI can communicate "not enough info yet".
 *
 * Item-level NA:
 *   If `item.item_allows_na` is true and the caller marks the whole item as NA
 *   via `itemNa: true`, the return is { score: null, itemNa: true }.
 *
 * Reporting fields (beyond the rubric):
 *   - `fullySwept`        — every indicator has an explicit Y/N/NA response.
 *                            The UI uses this to gate the "Complete" button so
 *                            observers are prompted to answer every indicator
 *                            before advancing (matches training practice and
 *                            the plan step 3 soft-block).
 *   - `inconsistentIndicatorIds` — when rule 1 (ANY L1 = Y → score 1) fires
 *                            but the observer also marked higher-level
 *                            indicators Yes, those indicator IDs are surfaced
 *                            so the UI can nudge reconciliation. The rubric
 *                            score is still 1 (final).
 *   - `allIndicatorsNa`   — every indicator is NA. Without this guard the
 *                            vacuous-truth `allYesAtLevel(…)` would cascade
 *                            to score 7 — clearly not what "nothing observed"
 *                            means. We return `score: null` instead and let
 *                            the UI prompt for either itemNa or real answers.
 */

export type ResponseMap = Record<string /* indicatorId */, IndicatorResponse>;

export interface ItemScoreResult {
  score: number | null;
  itemNa: boolean;
  /** True when the score is definitive given current responses. */
  complete: boolean;
  /** Ids of indicators still unanswered that could change the score. */
  blockingIndicatorIds: string[];
  /**
   * Ids of indicators whose response contradicts the computed score.
   * Populated only when score === 1 via rule 1 and the observer also
   * marked higher-level indicators as Yes. Informational — the rubric
   * score is unchanged, but the observer should reconcile.
   */
  inconsistentIndicatorIds: string[];
  /** True when every indicator on the item has a Y/N/NA response. */
  fullySwept: boolean;
  /** True when every indicator on the item is NA (no scoreable evidence). */
  allIndicatorsNa: boolean;
}

interface LevelTally {
  total: number; // indicators at this level
  answered: number; // Y or N (not NA, not missing)
  yes: number;
  no: number;
  na: number;
  missing: number;
  scoreable: number; // total - na
  missingIds: string[];
  yesIds: string[];
}

function tallyLevel(
  item: Item,
  level: ScoringLevel,
  responses: ResponseMap,
): LevelTally {
  let yes = 0;
  let no = 0;
  let na = 0;
  let missing = 0;
  const missingIds: string[] = [];
  const yesIds: string[] = [];
  const atLevel = item.indicators.filter((i) => i.level === level);
  for (const ind of atLevel) {
    const r = responses[ind.id];
    if (r === 'Y') {
      yes += 1;
      yesIds.push(ind.id);
    } else if (r === 'N') {
      no += 1;
    } else if (r === 'NA') {
      na += 1;
    } else {
      missing += 1;
      missingIds.push(ind.id);
    }
  }
  const total = atLevel.length;
  return {
    total,
    answered: yes + no,
    yes,
    no,
    na,
    missing,
    scoreable: total - na,
    missingIds,
    yesIds,
  };
}

function allYesAtLevel(tally: LevelTally): boolean {
  // Every scoreable (non-NA) indicator at the level answered Yes; no missing.
  // Edge: if EVERY indicator at the level is NA, there's nothing to judge;
  // by convention we treat that as "Yes" (the level imposes no blocking
  // requirements) — matches common FCCERS practice and keeps the algorithm
  // monotonic. The all-NA-across-every-level case is guarded separately in
  // `scoreItem()` via the `allIndicatorsNa` pre-check.
  if (tally.scoreable === 0) return true;
  return tally.missing === 0 && tally.yes === tally.scoreable;
}

function anyYesAtLevel(tally: LevelTally): boolean {
  return tally.yes > 0;
}

function atLeastHalfYes(tally: LevelTally): boolean {
  // "At least half" — denominator excludes NA (manual p. 11, Scoring System 8).
  // Threshold is ceil(scoreable / 2).
  if (tally.scoreable === 0) return true;
  const needed = Math.ceil(tally.scoreable / 2);
  return tally.yes >= needed;
}

type RuleResult = Pick<
  ItemScoreResult,
  'score' | 'complete' | 'blockingIndicatorIds'
>;

function runRules(tallies: Record<ScoringLevel, LevelTally>): RuleResult {
  // Rule 1: ANY L1 = Y → score 1. We can decide this regardless of other levels.
  if (anyYesAtLevel(tallies[1])) {
    return { score: 1, complete: true, blockingIndicatorIds: [] };
  }

  // If L1 still has missing responses, we can't rule out score 1.
  if (tallies[1].missing > 0) {
    return {
      score: null,
      complete: false,
      blockingIndicatorIds: tallies[1].missingIds,
    };
  }

  // From here on we know ALL L1 are No (or NA).
  // Walk down from score 7.

  // Rule 7
  if (
    tallies[7].missing === 0 &&
    allYesAtLevel(tallies[7]) &&
    allYesAtLevel(tallies[5]) &&
    allYesAtLevel(tallies[3])
  ) {
    return { score: 7, complete: true, blockingIndicatorIds: [] };
  }

  // Rule 6: all L5 Y AND ≥ half L7 Y
  if (
    tallies[5].missing === 0 &&
    allYesAtLevel(tallies[5]) &&
    allYesAtLevel(tallies[3]) &&
    tallies[7].missing === 0 &&
    atLeastHalfYes(tallies[7])
  ) {
    return { score: 6, complete: true, blockingIndicatorIds: [] };
  }

  // Rule 5: all L5 Y
  if (
    tallies[5].missing === 0 &&
    allYesAtLevel(tallies[5]) &&
    allYesAtLevel(tallies[3])
  ) {
    // We might still reach 6 or 7 if L7 missing. Report that.
    if (tallies[7].missing > 0) {
      return {
        score: 5,
        complete: false,
        blockingIndicatorIds: tallies[7].missingIds,
      };
    }
    return { score: 5, complete: true, blockingIndicatorIds: [] };
  }

  // Rule 4: all L3 Y AND ≥ half L5 Y
  if (
    tallies[3].missing === 0 &&
    allYesAtLevel(tallies[3]) &&
    tallies[5].missing === 0 &&
    atLeastHalfYes(tallies[5])
  ) {
    return { score: 4, complete: true, blockingIndicatorIds: [] };
  }

  // Rule 3: all L3 Y (L1 already known all N)
  if (tallies[3].missing === 0 && allYesAtLevel(tallies[3])) {
    // May still reach 4/5/6/7 if higher levels missing
    if (tallies[5].missing > 0) {
      return {
        score: 3,
        complete: false,
        blockingIndicatorIds: tallies[5].missingIds,
      };
    }
    return { score: 3, complete: true, blockingIndicatorIds: [] };
  }

  // Rule 2: ALL L1 = N AND ≥ half L3 Y
  // Rule 4+ all require "all L3 Y" which we just failed, so rule 2 is definitive.
  if (tallies[3].missing === 0 && atLeastHalfYes(tallies[3])) {
    return { score: 2, complete: true, blockingIndicatorIds: [] };
  }

  // L3 partially answered — can't decide 2/3 yet.
  if (tallies[3].missing > 0) {
    return {
      score: null,
      complete: false,
      blockingIndicatorIds: tallies[3].missingIds,
    };
  }

  // All L1 = N AND L3 fully answered but doesn't even clear "at least half" →
  // rule 4+ all require "all L3 Y" which we failed. Score 1 is the floor, and
  // it is final (rubric is monotonic). This mirrors FCCERS practice: if you
  // can't even reach 2, you score 1.
  return { score: 1, complete: true, blockingIndicatorIds: [] };
}

function computeInconsistentIds(
  base: RuleResult,
  tallies: Record<ScoringLevel, LevelTally>,
): string[] {
  // Rule 1 short-circuits to score 1 regardless of higher-level answers.
  // If the observer also marked any L3/L5/L7 indicator as Yes, surface
  // those IDs so the UI can prompt reconciliation. This does NOT change
  // the score — rubric says L1=Y wins.
  if (base.score !== 1) return [];
  if (!base.complete) return [];
  const ids: string[] = [];
  for (const level of [3, 5, 7] as const) {
    ids.push(...tallies[level].yesIds);
  }
  return ids;
}

export function scoreItem(
  item: Item,
  responses: ResponseMap,
  opts: { itemNa?: boolean } = {},
): ItemScoreResult {
  if (opts.itemNa && item.item_allows_na) {
    return {
      score: null,
      itemNa: true,
      complete: true,
      blockingIndicatorIds: [],
      inconsistentIndicatorIds: [],
      fullySwept: true,
      allIndicatorsNa: false,
    };
  }

  const tallies: Record<ScoringLevel, LevelTally> = {
    1: tallyLevel(item, 1, responses),
    3: tallyLevel(item, 3, responses),
    5: tallyLevel(item, 5, responses),
    7: tallyLevel(item, 7, responses),
  };

  const fullySwept =
    tallies[1].missing === 0 &&
    tallies[3].missing === 0 &&
    tallies[5].missing === 0 &&
    tallies[7].missing === 0;

  // Guard the pathological "every indicator NA" case — without this, every
  // level's `allYesAtLevel` returns true by vacuous truth and the rule
  // cascade lands on 7. "Nothing observed" should not score Excellent.
  const totalIndicators = item.indicators.length;
  const totalNa =
    tallies[1].na + tallies[3].na + tallies[5].na + tallies[7].na;
  const allIndicatorsNa = totalIndicators > 0 && totalNa === totalIndicators;
  if (allIndicatorsNa) {
    return {
      score: null,
      itemNa: false,
      complete: false,
      blockingIndicatorIds: [],
      inconsistentIndicatorIds: [],
      fullySwept: true, // all answered — just all NA
      allIndicatorsNa: true,
    };
  }

  const base = runRules(tallies);

  return {
    ...base,
    itemNa: false,
    inconsistentIndicatorIds: computeInconsistentIds(base, tallies),
    fullySwept,
    allIndicatorsNa: false,
  };
}

/** Handy for UI: which scored levels are "fully green" given current responses. */
export function levelsFullyYes(
  item: Item,
  responses: ResponseMap,
): Record<ScoringLevel, boolean> {
  const out: Record<ScoringLevel, boolean> = {
    1: false,
    3: false,
    5: false,
    7: false,
  };
  for (const level of SCORED_LEVELS) {
    out[level] = allYesAtLevel(tallyLevel(item, level, responses));
  }
  return out;
}

/**
 * Every indicator has an explicit Y/N/NA response. Useful for the Complete
 * button gate in the UI (we want observers to sweep the whole item, not
 * just the minimum needed to trip a rule).
 */
export function isItemFullySwept(
  item: Item,
  responses: ResponseMap,
): boolean {
  return item.indicators.every(
    (ind: Indicator) => responses[ind.id] !== undefined,
  );
}
